import { ObjectType } from 'phoenix/domain';

/**
 * Aggregates required file data and metadata for uploading a new
 * file to sharebase.
 */
export interface UploadFile {
    /*
     * The browser file object that contains the data to upload
     */
    file:Blob,
    /**
     * The uri that will be used to commit the document after the
     * file content has completed uploading.
     */
    uploadUri:string;
    /**
     * Id of the object to upload the file to, can be either a
     * folder id or a document id.
     */
    objectId:number;
    /**
     * Indicates what type of object the @see objectId represents.
     */
    objectType:ObjectType;
    /**
     * The name of the document that will be created.
     */
    name:string;
}

/**
 * Configuration options that determine how files will be uploaded.
 */
export interface UploadOptions {
    /**
     * Max number of files that can be uploaded at one time.
     */
    maxParallelUploads: number;
    /**
     * Maximum size (in bytes) for files to be uploaded using multipart, if a file
     * is bigger than this, it will be streamed to the server.
     */
    chunkThreshold: number;
    /**
     * The size of chunks (in bytes) to use if the file needs to be streamed to the server.
     */
    chunkSize: number;
    /**
     * Maximum number of times to retry uploading a file before failing.
     */
    maxRetries: number;
    /**
     * Maximum allowed file size. Anything above this will not be uploaded to the server.
     */
    maxFileSizeInBytes: number;
}

/**
 * Represents the combined upload status of all files being uploaded at
 * any given time.
 */
export interface UploadStatus {
    /**
     * Number of files waiting to be uploaded.
     */
    filesQueued: number;
    /**
     * Number of files currently being uploaded.
     */
    filesInProcess: number;
    /**
     * The sum of @see filesQueued and @see filesInProcess
     */
    activeUploads: number;
    /**
     * The sum of the file size of all files currently queued.
     */
    totalBytesQueued: number;
    /**
     * The sume of the number of bytes that have been successfully uploaded.
     */
    bytesCompleted: number;
    /**
     * @see bytesCompleted represented as a percentage of @see totalBytesQueued
     */
    percentCompleted: number;
    /**
     * True if the service is in cancel mode when queried.
     */
    cancelMode: boolean;
}

export interface ProgressEventListener {
    (e: ProgressEvent): void;
}

/**
 * File upload state machine values.
 */
export type UploadState = 'queued' | 'uploading' | 'done' | 'cancelled' | 'error';

/**
 * Constants for the acceptable values for @see UploadState
 */
export const uploadStates = {
    queued: 'queued' as UploadState,
    uploading: 'uploading' as UploadState,
    done: 'done' as UploadState,
    cancelled: 'cancelled' as UploadState,
    error: 'error' as UploadState
};




// WEBPACK FOOTER //
// ./src/phoenix/domain/upload-types.ts